import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Users, Eye, EyeOff, Shield, Mail, Phone, MapPin, Calendar, User, Settings, Activity, Lock, Truck, Download, ChevronLeft, ChevronRight, X, Save, FileText, Printer } from 'lucide-react';
import { apiService } from '../services/api';
import RoleBasedComponent from './RoleBasedComponent';
import * as XLSX from 'xlsx';

import { User as UserType } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import { menuItems } from '../data/menuData';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';

const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('users', setSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [userAssets, setUserAssets] = useState<any>(null);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnNotes, setReturnNotes] = useState('');
  const [returnLoading, setReturnLoading] = useState(false);
  const [returnResult, setReturnResult] = useState<{formId: number; formNumber: string} | null>(null);
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterLocation, setFilterLocation] = useState<string>('all');
  const [showPassword, setShowPassword] = useState(false);
  const [usersData, setUsersData] = useState<UserType[]>([]);
  const [departmentsData, setDepartmentsData] = useState<string[]>([]);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [newDepartment, setNewDepartment] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<UserType[]>([]);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  const [companiesData, setCompaniesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [itemsPerPage] = useState(20);
  const [activeTab, setActiveTab] = useState('users');
  const [rolePermissions, setRolePermissions] = useState<any>({});
  const [backendRolePermissions, setBackendRolePermissions] = useState<any>({});
  
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    role: 'calisan' as UserType['role'],
    location_id: 0,
    company_id: 0,
    phone: '',
    title: '',
    department: '',
    address: '',
    birth_date: '',
    hire_date: '',
    emergency_contact: '',
    emergency_phone: '',
    notes: '',
    password: '',
    is_active: true,
    locations: [] as number[]
  });

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  // Bildirimden belirli bir kullanıcı açma
  React.useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'users' || !e.detail?.entityId) return;
      try {
        const user = await apiService.getUserById(e.detail.entityId);
        if (user) showUserDetail(user);
      } catch (err) { console.error('[openEntityById users]', err); }
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  React.useEffect(() => {
    if (activeTab === 'permissions') {
      loadBackendRolePermissions();
    }
  }, [activeTab]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Arama yapıldığında ilk sayfaya dön
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const loadBackendRolePermissions = async () => {
    try {
      const response = await apiService.getRolePermissions();
      const permissionsMap: any = {};
      
      response.forEach((perm: any) => {
        const key = `${perm.menu_id}_${perm.role}_${perm.permission_type}`;
        permissionsMap[key] = perm.granted;
      });
      
      setBackendRolePermissions(permissionsMap);
    } catch (error) {
      console.error('Error loading backend role permissions:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersResponse, locationsResponse, companiesResponse, departmentsResponse] = await Promise.all([
        apiService.getUsers(),
        apiService.getLocations(),
        apiService.getCompanies(),
        apiService.getDepartments()
      ]);
      setUsersData(Array.isArray(usersResponse) ? usersResponse : []);
      setLocationsData(Array.isArray(locationsResponse) ? locationsResponse : []);
      setCompaniesData(Array.isArray(companiesResponse) ? companiesResponse : []);
      setDepartmentsData(Array.isArray(departmentsResponse) ? departmentsResponse : []);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('users.errLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddDepartment = async () => {
    if (!newDepartment.trim()) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('users.errDeptEmpty'));
      return;
    }

    if (departmentsData.includes(newDepartment.trim())) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('users.errDeptExists'));
      return;
    }

    try {
      // Departmanı listeye ekle
      setDepartmentsData([...departmentsData, newDepartment.trim()]);
      setNewDepartment('');
      setShowAddDepartmentModal(false);
      showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('users.successDeptAdded'));
    } catch (error) {
      console.error('Error adding department:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('users.errDeptAdd'));
    }
  };

  const roleOptions = [
    { value: 'calisan', label: 'Çalışan', color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' },
    { value: 'teknisyen', label: 'Teknisyen', color: 'bg-primary/20 text-primary-dark' },
    { value: 'admin', label: 'Admin', color: 'bg-green-100 text-green-800' },
    { value: 'superadmin', label: 'Super Admin', color: 'bg-purple-100 text-purple-800' }
  ];

  // Admin kullanıcılar için superadmin seçeneğini gizle
  const getAvailableRoles = () => {
    if (currentUser?.role === 'admin') {
      return roleOptions.filter(role => role.value !== 'superadmin');
    }
    return roleOptions;
  };

  // Filtreleme ve pagination için useEffect
  React.useEffect(() => {
    applyFilters();
  }, [usersData, debouncedSearchTerm, filterRole, filterStatus, filterLocation, currentPage]);

  const applyFilters = () => {
    let filtered = [...usersData];

    // Arama filtresi
    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        (user.name && String(user.name).toLowerCase().includes(searchLower)) ||
        (user.email && String(user.email).toLowerCase().includes(searchLower)) ||
        (user.phone && String(user.phone).includes(debouncedSearchTerm)) ||
        (user.title && String(user.title).toLowerCase().includes(searchLower)) ||
        (user.department && String(user.department).toLowerCase().includes(searchLower))
      );
    }

    // Rol filtresi
    if (filterRole !== 'all') {
      filtered = filtered.filter(user => user.role === filterRole);
    }

    // Durum filtresi
    if (filterStatus !== 'all') {
      const isActive = filterStatus === 'active';
      filtered = filtered.filter(user => user.is_active === isActive);
    }

    // Lokasyon filtresi
    if (filterLocation !== 'all') {
      filtered = filtered.filter(user => user.location_id === parseInt(filterLocation));
    }

    // Pagination hesaplama
    const total = filtered.length;
    const totalPages = Math.ceil(total / itemsPerPage);
    setTotalPages(totalPages);
    setTotalUsers(total);

    // Sayfa değiştiğinde currentPage'i sıfırla
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }

    // Mevcut sayfa için verileri al
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedUsers = filtered.slice(startIndex, endIndex);

    setFilteredUsers(paginatedUsers);
  };

  const logActivity = (action: string, description: string, userName?: string) => {
    // Show toast notification
    showGlobalNotification.success(
      'Kullanıcı İşlemi',
      description
    );
  };

  const handleEdit = async (user: UserType) => {
    setEditingUser(user);
    setUserData({
      name: user.name,
      email: user.email,
      role: user.role,
      location_id: user.location_id || 0,
      company_id: user.company_id || 0,
      phone: user.phone || '',
      title: user.title || '',
      department: user.department || '',
      address: user.address || '',
      birth_date: user.birth_date ? user.birth_date.split('T')[0] : '',
      hire_date: user.hire_date ? user.hire_date.split('T')[0] : '',
      emergency_contact: user.emergency_contact || '',
      emergency_phone: user.emergency_phone || '',
      notes: user.notes || '',
      password: '',
      is_active: user.is_active !== false,
      locations: (user as any).locations ? (user as any).locations.map((loc: any) => loc.id) : []
    });
    setShowModal(true);
  };

  const handleAdd = async () => {
    setEditingUser(null);
    setUserData({
      name: '',
      email: '',
      role: 'calisan',
      location_id: 0,
      company_id: 0,
      phone: '',
      title: '',
      department: '',
      address: '',
      birth_date: '',
      hire_date: '',
      emergency_contact: '',
      emergency_phone: '',
      notes: '',
      password: '',
      is_active: true,
      locations: []
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    // Validate required fields
    if (!userData.name || !userData.email) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('users.errRequiredFields'));
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email.trim())) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('users.errInvalidEmail'));
      return;
    }

    // Validate location and company
    if (!userData.location_id || userData.location_id === 0) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('users.errNoLocation'));
      return;
    }

    if (!userData.company_id || userData.company_id === 0) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('users.errNoCompany'));
      return;
    }
    
    // Check for duplicate names
    const duplicateName = usersData.find(user =>
      user.name && userData.name && String(user.name).toLowerCase() === String(userData.name).toLowerCase() &&
      (!editingUser || user.id !== editingUser.id)
    );
    
    if (duplicateName) {
      if (confirm(`"${userData.name}" isimli bir kullanıcı zaten mevcut. Devam etmek istediğinizden emin misiniz?`)) {
        // User confirmed, continue
      } else {
        return;
      }
    }
    
    try {
      if (editingUser) {
        // Update existing user - remove password field
        const { password, ...updateData } = userData;
        await apiService.updateUser(editingUser.id, updateData);
        
        // User permissions API kaldırıldı
        
        // Reload users data to get updated information including locations
        await loadData();
        
        logActivity(
          'update_user',
          `${currentUser?.name} "${userData.name}" kullanıcısını güncelledi`,
          userData.name
        );
      } else {
        // Add new user
        const newUser = await apiService.createUser(userData);
        
        // User permissions API kaldırıldı
        
        // Reload users data to get the new user with proper ID
        await loadData();
        
        logActivity(
          'create_user',
          `${currentUser?.name} "${userData.name}" kullanıcısını oluşturdu`,
          userData.name
        );
      }
      
      setShowModal(false);
      setUserData({
        name: '',
        email: '',
        role: 'calisan',
        location_id: 0,
        company_id: 0,
        phone: '',
        title: '',
        department: '',
        address: '',
        birth_date: '',
        hire_date: '',
        emergency_contact: '',
        emergency_phone: '',
        notes: '',
        password: '',
        is_active: true,
        locations: []
      });
    } catch (error: any) {
      console.error('Error saving user:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('users.errSave'));
    }
  };

  const exportUsers = async () => {
    if (!confirm('Kullanıcı listesini Excel dosyası olarak indirmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setLoading(true);
      const rolesParam = filterRole !== 'all' ? filterRole : '';
      const response = await apiService.exportUsers(searchTerm, rolesParam);
      
      if (response.success && response.data) {
        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(response.data);
        
        // Set column widths
        const colWidths = [
          { wch: 25 }, // Ad Soyad
          { wch: 30 }, // E-posta
          { wch: 15 }, // Rol
          { wch: 15 }, // Telefon
          { wch: 20 }, // Unvan
          { wch: 20 }, // Departman
          { wch: 25 }, // Lokasyon
          { wch: 30 }, // Adres
          { wch: 15 }, // Doğum Tarihi
          { wch: 15 }, // İşe Giriş Tarihi
          { wch: 25 }, // Acil Durum İletişim
          { wch: 15 }, // Acil Durum Telefon
          { wch: 30 }, // Notlar
          { wch: 10 }, // Durum
          { wch: 15 }  // Oluşturulma Tarihi
        ];
        ws['!cols'] = colWidths;
        
        XLSX.utils.book_append_sheet(wb, ws, 'Kullanıcılar');
        
        // Generate filename with current date
        const currentDate = new Date().toISOString().split('T')[0];
        const filename = `kullanicilar_${currentDate}.xlsx`;
        
        // Save file
        XLSX.writeFile(wb, filename);
        
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('users.exportSuccess').replace('{count}', String(response.total)));
      } else {
        throw new Error('Export verisi alınamadı');
      }
    } catch (error: any) {
      console.error('Error exporting users:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('users.errExport'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const userToDelete = usersData.find(u => u.id === id);
    if (!userToDelete) return;
    
    // Prevent deleting current user
    if (id === currentUser?.id) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('users.errCannotDeleteSelf'));
      return;
    }
    
    if (confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      try {
        // Call API to delete user
        await apiService.deleteUser(id);
        
        // Update local state after successful API call
        const updatedUsers = (usersData || []).filter(user => user.id !== id);
        setUsersData(updatedUsers);
        
        logActivity(
          'delete_user',
          `${currentUser?.name} "${userToDelete.name}" kullanıcısını sildi`,
          userToDelete.name
        );
      } catch (error: any) {
        console.error('Error deleting user:', error);
        showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('users.errDelete'));
      }
    }
  };

  const showUserDetail = async (user: UserType) => {
    setSelectedUser(user);
    setUserAssets(null);
    setReturnResult(null);
    setShowDetailModal(true);
    setAssetsLoading(true);
    try {
      const assets = await apiService.getUserAssets(user.id);
      setUserAssets(assets);
    } catch (e) {
      setUserAssets({});
    } finally {
      setAssetsLoading(false);
    }
  };

  const handleOpenReturnModal = () => {
    setReturnNotes('');
    setReturnResult(null);
    setShowReturnModal(true);
  };

  const handleReturnAllAssets = async () => {
    if (!selectedUser) return;
    setReturnLoading(true);
    try {
      const result = await apiService.returnAllUserAssets(selectedUser.id, { notes: returnNotes });
      setReturnResult({ formId: result.formId, formNumber: result.formNumber });
      // Zimmet listesini güncelle
      const assets = await apiService.getUserAssets(selectedUser.id);
      setUserAssets(assets);
    } catch (e: any) {
      showGlobalNotification(e?.message || 'Zimmet iade işlemi başarısız.', 'error');
    } finally {
      setReturnLoading(false);
    }
  };

  const toggleUserStatus = async (user: UserType) => {
    const newStatus = !user.is_active;
    const statusText = newStatus ? 'aktif' : 'pasif';
    
    if (confirm(`${user.name} kullanıcısını ${statusText} yapmak istediğinizden emin misiniz?`)) {
      try {
        // Update user status via API
        await apiService.updateUserStatus(user.id, newStatus);
        
        // Update local state
        const updatedUsers = usersData.map(u => 
          u.id === user.id ? { ...u, is_active: newStatus } : u
        );
        setUsersData(updatedUsers);
        
        showGlobalNotification.success(
          'Durum Değiştirildi', 
          `${user.name} kullanıcısının durumu ${statusText} yapıldı`
        );
        
        logActivity(
          'toggle_user_status',
          `${currentUser?.name} "${user.name}" kullanıcısını ${statusText} yaptı`,
          user.name
        );
      } catch (error: any) {
        console.error('Error toggling user status:', error);
        showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('users.errStatusChange'));
      }
    }
  };

  const resetPassword = async (user: UserType) => {
    if (confirm(`${user.name} kullanıcısının parolasını sıfırlamak istediğinizden emin misiniz?\n\nYeni parola otomatik oluşturulacak ve size gösterilecektir.`)) {
      setResetPasswordLoading(true);
      try {
        const response = await apiService.resetUserPassword(user.id);

        // Show success message with new password
        const message = `Parola başarıyla sıfırlandı!\n\nKullanıcı: ${user.name}\nE-posta: ${response.userEmail}\nYeni Parola: ${response.newPassword}\n\nBu parolayı güvenli bir yerde saklayın ve kullanıcıya iletin.`;

        if (confirm(message + '\n\nParolayı panoya kopyalamak ister misiniz?')) {
          navigator.clipboard.writeText(response.newPassword).then(() => {
            showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', language === 'en' ? 'Password copied to clipboard' : 'Parola panoya kopyalandı');
          }).catch(() => {
            showGlobalNotification.warning(language === 'en' ? 'Warning' : 'Uyarı', language === 'en' ? 'Password could not be copied, please save it manually' : 'Parola kopyalanamadı, lütfen manuel olarak kaydedin');
          });
        }

        showGlobalNotification.success('Başarılı', `${user.name} kullanıcısının parolası sıfırlandı`);

        logActivity(
          'reset_password',
          `${currentUser?.name} "${user.name}" kullanıcısının parolasını sıfırladı`,
          user.name
        );
      } catch (error: any) {
        console.error('Error resetting password:', error);
        showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('users.errPasswordReset'));
      } finally {
        setResetPasswordLoading(false);
      }
    }
  };


  const sendLoginReminder = async (user: UserType) => {
    if (confirm(`${user.name} kullanıcısına giriş bilgilerini hatırlatma e-postası göndermek istediğinizden emin misiniz?`)) {
      try {
        // API call to send login reminder email
        await apiService.sendLoginReminder(user.id);
        
        showGlobalNotification.success(
          t('users.emailSent'),
          `${user.name} ${language === 'en' ? 'login reminder email sent' : 'kullanıcısına giriş bilgileri hatırlatma e-postası gönderildi'}`
        );
        
        logActivity(
          'send_login_reminder',
          `${currentUser?.name} "${user.name}" kullanıcısına giriş bilgileri hatırlatma e-postası gönderdi`,
          user.name
        );
      } catch (error: any) {
        console.error('Error sending login reminder:', error);
        showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('users.errEmailSend'));
      }
    }
  };


  const getRoleColor = (role: string) => {
    return roleOptions.find(r => r.value === role)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getRoleLabel = (role: string) => {
    return roleOptions.find(r => r.value === role)?.label || role;
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    const array = new Uint32Array(12);
    crypto.getRandomValues(array);
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(array[i] % chars.length);
    }
    setUserData({...userData, password});
  };

  const handleRolePermissionChange = (menuId: string, role: string, permission: string, checked: boolean) => {
    const key = `${menuId}_${role}_${permission}`;
    
    setRolePermissions(prev => ({
      ...prev,
      [key]: checked
    }));
    
    setBackendRolePermissions(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  // Search and filter functions
  const handleFilterChange = () => {
    setCurrentPage(1); // Filtre değiştiğinde ilk sayfaya dön
    applyFilters();
  };

  // Pagination functions
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const saveRolePermissions = async () => {
    try {
      // Rol yetkilerini backend'e kaydet
      for (const [key, value] of Object.entries(rolePermissions)) {
        const [menuId, role, permission] = key.split('_');
        
        await apiService.updateRolePermission({
          role,
          menuId,
          permission,
          granted: value as boolean
        });
      }
      
      showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('users.successPermSaved'));
      
      // Backend'den yeniden yükle
      await loadBackendRolePermissions();
    } catch (error: any) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('users.errPermSave'));
    }
  };
  
  const printUserCargoLabel = (user: UserType) => {
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
    // Kullanıcının seçili lokasyonunun adresi; yoksa kullanıcıya girilmiş adres
    const addressOf = (u: any) => {
      const loc = locationsData.find(l => l.id === u?.location_id);
      return (loc?.address || u?.address || '').trim();
    };
    const party = (title: string, u: any) => {
      const address = addressOf(u);
      return `
        <div style="margin-bottom: 20px; border: 1px solid #ccc; padding: 10px;">
          <h3 style="margin: 0 0 10px 0; color: #333;">${title}</h3>
          <div><strong>${esc(u?.name)}</strong></div>
          ${u?.phone ? `<div>Tel: ${esc(u.phone)}</div>` : ''}
          ${address ? `<div style="white-space: pre-line;">${esc(address)}</div>` : ''}
        </div>`;
    };

    const cargoContent = `
      <div style="font-family: Arial; padding: 20px; border: 2px solid #000; width: 400px;">
        <h2 style="text-align: center; margin: 0 0 20px 0; background: #000; color: white; padding: 10px;">KARGO ETİKETİ</h2>
        ${party('GÖNDEREN', currentUser)}
        ${party('ALICI', user)}
      </div>
    `;

    const printWindow = window.open('', '_blank');
    printWindow?.document.write(`
      <html>
        <head><title>Kargo Etiketi - ${esc(user.name)}</title></head>
        <body>${cargoContent}</body>
      </html>
    `);
    printWindow?.document.close();
    printWindow?.print();
  };

  // Kullanıcının tüm zimmetlerini envanter bilgileriyle tek bir yazdırılabilir listede gösterir
  const printUserAssets = () => {
    if (!selectedUser || !userAssets) return;
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
    const locale = language === 'en' ? 'en-US' : 'tr-TR';
    const rows: { type: string; brandModel: string; serial: string; inventory: string; detail: string; qty: number }[] = [
      ...(userAssets.computers || []).map((c: any) => ({
        type: t('users.detailAssetsComputers'), brandModel: `${c.brand || ''} ${c.model || ''}`, serial: c.serial_number,
        inventory: c.inventory_number, detail: c.location_name, qty: 1
      })),
      ...(userAssets.mobilePhones || []).map((p: any) => ({
        type: t('users.detailAssetsMobilePhones'), brandModel: `${p.brand || ''} ${p.model || ''}`,
        serial: [p.imei1 && `IMEI: ${p.imei1}`, p.imei2 && `IMEI 2: ${p.imei2}`, p.serial_number && `SN: ${p.serial_number}`].filter(Boolean).join('<br>'),
        inventory: p.inventory_number, detail: [p.gsm_number, p.company_name].filter(Boolean).join(' · '), qty: 1
      })),
      ...(userAssets.handheldTerminals || []).map((h: any) => ({
        type: language === 'en' ? 'IoT Equipment' : 'IoT Ekipmanı', brandModel: `${h.brand || ''} ${h.model || ''}`,
        serial: [h.serial_number && `SN: ${h.serial_number}`, h.ip_address && `IP: ${h.ip_address}`].filter(Boolean).join('<br>'),
        inventory: h.inventory_number, detail: h.location_name, qty: 1
      })),
      ...(userAssets.monitors || []).map((m: any) => ({
        type: t('users.detailAssetsMonitors'), brandModel: `${m.brand || ''} ${m.model || ''}`, serial: m.serial_number,
        inventory: m.inventory_number, detail: m.location_name, qty: 1
      })),
      ...(userAssets.gsmLines || []).map((g: any) => ({
        type: t('users.detailAssetsGsm'), brandModel: g.phone_number, serial: '', inventory: '',
        detail: [g.tariff_name, g.company_name].filter(Boolean).join(' · '), qty: 1
      })),
      ...(userAssets.equipmentAssignments || []).map((e: any) => ({
        type: e.category_name || t('users.detailAssetsEquipment'), brandModel: `${e.brand || ''} ${e.model || ''}`, serial: e.serial_number,
        inventory: e.inventory_number,
        detail: e.assigned_date ? `${t('users.detailAssetsColAssignDate')}: ${new Date(e.assigned_date).toLocaleDateString(locale)}` : '',
        qty: e.quantity || 1
      }))
    ];
    if (rows.length === 0) {
      showGlobalNotification.info(t('users.printAssetsTitle'), t('users.detailAssetsEmpty'));
      return;
    }
    // serial alanı IMEI satır sonları için HTML içerir; diğer değerler escape edilir
    const tableRows = rows.map((r, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${esc(r.type)}</td>
        <td>${esc(r.brandModel.trim()) || '-'}</td>
        <td>${r.serial ? r.serial.split('<br>').map(esc).join('<br>') : '-'}</td>
        <td>${esc(r.inventory) || '-'}</td>
        <td>${esc(r.detail) || '-'}</td>
        <td class="c">${r.qty}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="${language}">
<head>
<meta charset="UTF-8" />
<title>${esc(t('users.printAssetsTitle'))} - ${esc(selectedUser.name)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, sans-serif; color: #111; margin: 0; padding: 24px; font-size: 12px; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .meta { color: #555; margin-bottom: 16px; }
  .user { display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px 24px; border: 1px solid #ccc; padding: 10px 12px; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #bbb; padding: 6px 8px; text-align: left; vertical-align: top; }
  th { background: #f0f0f0; }
  td.c, th.c { text-align: center; }
  .total { margin-top: 8px; text-align: right; font-weight: bold; }
  .no-print { text-align: center; margin-top: 24px; }
  .no-print button { background: #4f46e5; color: #fff; border: 0; padding: 10px 24px; border-radius: 6px; font-size: 14px; cursor: pointer; }
  @media print { body { padding: 0; } .no-print { display: none; } tr { page-break-inside: avoid; } }
</style>
</head>
<body>
  <h1>${esc(t('users.printAssetsTitle'))}</h1>
  <div class="meta">${esc(new Date().toLocaleString(locale))} · ${esc(t('users.printAssetsPrintedBy'))}: ${esc(currentUser?.name)}</div>
  <div class="user">
    <div><strong>${esc(t('users.printAssetsUser'))}:</strong> ${esc(selectedUser.name)}</div>
    <div><strong>E-posta:</strong> ${esc(selectedUser.email)}</div>
    <div><strong>${esc(t('users.printAssetsDepartment'))}:</strong> ${esc(selectedUser.department) || '-'}</div>
    <div><strong>${esc(t('users.printAssetsTitleField'))}:</strong> ${esc(selectedUser.title) || '-'}</div>
    <div><strong>${esc(t('users.printAssetsLocation'))}:</strong> ${esc(selectedUser.location?.name || locationsData.find(l => l.id === selectedUser.location_id)?.name) || '-'}</div>
  </div>
  <table>
    <thead>
      <tr>
        <th class="c">#</th>
        <th>${esc(t('users.detailAssetsColType'))}</th>
        <th>${esc(t('users.detailAssetsColBrandModel'))}</th>
        <th>${esc(t('users.printAssetsColSerial'))}</th>
        <th>${esc(t('users.detailAssetsColInventory'))}</th>
        <th>${esc(t('users.printAssetsColDetail'))}</th>
        <th class="c">${esc(t('users.detailAssetsColQty'))}</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
  <div class="total">${esc(t('users.printAssetsTotal', { count: rows.length }))}</div>
  <div class="no-print"><button onclick="window.print()">🖨️ ${esc(t('users.printAssetsPrintBtn'))}</button></div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col space-y-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">{t('users.title')}</h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{t('users.subtitle')}</p>
          </div>
          {activeTab === 'users' && (
            <div className="flex flex-col sm:flex-row gap-3">
              <RoleBasedComponent requiredPermissions={['users.create']} hideIfNoAccess>
                <button
                  onClick={handleAdd}
                  className="flex items-center justify-center w-full sm:w-auto px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-base font-medium"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  {t('users.newUser')}
                </button>
              </RoleBasedComponent>
              <RoleBasedComponent requiredRoles={['admin', 'superadmin']}>
                <button
                  onClick={exportUsers}
                  disabled={loading}
                  className="flex items-center justify-center w-full sm:w-auto px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Excel Export
                </button>
              </RoleBasedComponent>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-3 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'users'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            {t('users.tabUsers')}
          </button>
          <RoleBasedComponent requiredRoles={['superadmin']}>
            <button
              onClick={() => setActiveTab('permissions')}
              className={`py-3 px-2 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'permissions'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              <Shield className="w-4 h-4 inline mr-2" />
              {t('users.tabPermissions')}
            </button>
          </RoleBasedComponent>
        </nav>
      </div>

      {activeTab === 'users' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('users.statTotal')}</p>
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{usersData.length}</p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-600" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('users.statActive')}</p>
                  <p className="text-lg sm:text-2xl font-bold text-green-600">
                    {(usersData || []).filter(u => u.is_active !== false).length}
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <User className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('users.statOnline')}</p>
                  <p className="text-lg sm:text-2xl font-bold text-primary">
                    {(usersData || []).filter(u => u.is_online === true).length}
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Activity className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('users.statAdmin')}</p>
                  <p className="text-lg sm:text-2xl font-bold text-purple-600">
                    {(usersData || []).filter(u => u.role === 'admin' || u.role === 'superadmin').length}
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

      {/* Filters */}
      <div className="space-y-4 mb-6">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('users.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-full text-base"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select
            value={filterRole}
            onChange={(e) => {
              setFilterRole(e.target.value);
              handleFilterChange();
            }}
            className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base"
          >
            <option value="all">{t('users.filterAllRoles')}</option>
            {roleOptions.map(role => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base"
          >
            <option value="all">{t('users.filterAllStatuses')}</option>
            <option value="active">{t('users.filterActive')}</option>
            <option value="inactive">{t('users.filterInactive')}</option>
          </select>

          <select
            value={filterLocation}
            onChange={(e) => {
              setFilterLocation(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base"
          >
            <option value="all">{t('users.filterAllLocations')}</option>
            {locationsData.map(location => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
        </div>
      </div>

          {/* Users List - Desktop Table View */}
          <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.colUser')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.colContact')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.colRole')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.colLocation')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.colStatus')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.colLastLogin')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('users.colActions')}
                  </th>
                </tr>
              </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-12 text-center">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500 dark:text-gray-400 text-lg">{t('users.notFound')}</p>
                        <p className="text-gray-400 text-sm mt-1">{t('users.notFoundDesc')}</p>
                      </td>
                    </tr>
                  ) : filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                      <td className="px-6 py-3">
                        <div className="flex items-center">
                          <div className="relative flex-shrink-0">
                            <img
                              className="w-10 h-10 rounded-full"
                              src={user.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'}
                              alt={user.name}
                            />
                            {!!user.is_online && user.is_active !== false && (
                              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                            )}
                            {!user.is_online && user.is_active !== false && (
                              <div className="absolute bottom-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
                            )}
                            {user.is_active === false && (
                              <div className="absolute bottom-0 right-0 w-3 h-3 bg-gray-500 rounded-full border-2 border-white"></div>
                            )}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {user.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {user.title}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <div className="text-sm text-gray-900 dark:text-gray-100">{user.email}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{user.phone}</div>
                      </td>
                      <td className="px-6 py-3">
                        <span className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {user.location?.name || t('users.unassigned')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-2 ${user.is_active !== false ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {user.is_active !== false ? t('users.statusActive') : t('users.statusInactive')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {user.last_login ? new Date(user.last_login).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR') : t('users.never')}
                      </td>
                      <td className="px-6 py-3 text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <RoleBasedComponent requiredPermissions={['users.view']}>
                            <button
                              onClick={() => showUserDetail(user)}
                              className="text-primary hover:text-primary-dark"
                              title="Detayları Görüntüle"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </RoleBasedComponent>
                          <RoleBasedComponent requiredPermissions={['users.update']}>
                            <button
                              onClick={() => handleEdit(user)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Düzenle"
                              aria-label={`${user.name} kullanıcısını düzenle`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </RoleBasedComponent>
                          <RoleBasedComponent requiredPermissions={['users.update']}>
                            {currentUser?.id !== user.id && (
                              <button
                                onClick={() => resetPassword(user)}
                                className="text-orange-600 hover:text-orange-900"
                                title="Parola Sıfırla"
                                aria-label={`${user.name} kullanıcısının parolasını sıfırla`}
                                disabled={resetPasswordLoading}
                              >
                                <Lock className="w-4 h-4" />
                              </button>
                            )}
                          </RoleBasedComponent>
                          <RoleBasedComponent requiredPermissions={['users.update']}>
                            <button
                              onClick={() => toggleUserStatus(user)}
                              className={`${user.is_active !== false ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                              title={user.is_active !== false ? 'Pasif Yap' : 'Aktif Yap'}
                              aria-label={`${user.name} kullanıcısını ${user.is_active !== false ? 'pasif yap' : 'aktif yap'}`}
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                          </RoleBasedComponent>
                          <RoleBasedComponent requiredPermissions={['users.update']}>
                            <button
                              onClick={() => sendLoginReminder(user)}
                              className="text-primary hover:text-primary-dark"
                              title="Giriş Bilgilerini Hatırlat"
                              aria-label={`${user.name} kullanıcısına giriş bilgilerini hatırlat`}
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                          </RoleBasedComponent>
                          <button
                            onClick={() => printUserCargoLabel(user)}
                            className="text-green-600 hover:text-green-900"
                            title="Kargo Etiketi"
                            aria-label={`${user.name} kullanıcısı için kargo etiketi yazdır`}
                          >
                            <Truck className="w-4 h-4" />
                          </button>
                          <RoleBasedComponent requiredPermissions={['users.delete']}>
                            <button
                              onClick={() => handleDelete(user.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Sil"
                              aria-label={`${user.name} kullanıcısını sil`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </RoleBasedComponent>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {t('users.paginationInfo').replace('{total}', String(totalUsers)).replace('{start}', String(((currentPage - 1) * itemsPerPage) + 1)).replace('{end}', String(Math.min(currentPage * itemsPerPage, totalUsers)))}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Önceki sayfa"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Sadece mevcut sayfa ve etrafındaki sayfaları göster
                  if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          page === currentPage
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2 text-gray-500 dark:text-gray-400">...</span>;
                  }
                  return null;
                })}
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Sonraki sayfa"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Users List - Mobile Card View */}
          <div className="lg:hidden space-y-4">
            {filteredUsers.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 text-lg">{t('users.notFound')}</p>
                <p className="text-gray-400 text-sm mt-1">{t('users.notFoundDesc')}</p>
              </div>
            ) : filteredUsers.map((user) => (
              <div key={user.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                {/* User Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center flex-1">
                    <div className="relative">
                      <img
                        className="w-12 h-12 rounded-full"
                        src={user.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'}
                        alt={user.name}
                      />
                      {!!user.is_online && user.is_active !== false && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                      )}
                      {!user.is_online && user.is_active !== false && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
                      )}
                      {user.is_active === false && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-gray-500 rounded-full border-2 border-white"></div>
                      )}
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">{user.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{user.title}</p>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${getRoleColor(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-2 ${user.is_active !== false ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {user.is_active !== false ? t('users.statusActive') : t('users.statusInactive')}
                    </span>
                  </div>
                </div>

                {/* User Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="truncate">{user.email}</span>
                  </div>
                  {user.phone && (
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Phone className="w-4 h-4 mr-2 text-gray-400" />
                      <span>{user.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                    <span>{user.location?.name || t('users.unassigned')}</span>
                  </div>
                  {user.last_login && (
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                      <span>{language === 'en' ? 'Last login: ' : 'Son giriş: '}{new Date(user.last_login).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                  <RoleBasedComponent requiredPermissions={['users.view']}>
                    <button
                      onClick={() => showUserDetail(user)}
                      className="flex items-center px-3 py-2 text-xs bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      {t('users.detailBtn')}
                    </button>
                  </RoleBasedComponent>
                  <RoleBasedComponent requiredPermissions={['users.update']}>
                    <button
                      onClick={() => handleEdit(user)}
                      className="flex items-center px-3 py-2 text-xs bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      {t('users.editBtn')}
                    </button>
                  </RoleBasedComponent>
                  <RoleBasedComponent requiredPermissions={['users.update']}>
                    {currentUser?.id !== user.id && (
                      <button
                        onClick={() => resetPassword(user)}
                        className="flex items-center px-3 py-2 text-xs bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-colors"
                      >
                        <Lock className="w-3 h-3 mr-1" />
                        {t('users.passwordBtn')}
                      </button>
                    )}
                  </RoleBasedComponent>
                  <RoleBasedComponent requiredPermissions={['users.update']}>
                    <button
                      onClick={() => toggleUserStatus(user)}
                      className={`flex items-center px-3 py-2 text-xs rounded-lg transition-colors ${
                        user.is_active !== false 
                          ? 'bg-red-50 text-red-600 hover:bg-red-100' 
                          : 'bg-green-50 text-green-600 hover:bg-green-100'
                      }`}
                    >
                      <Settings className="w-3 h-3 mr-1" />
                      {user.is_active !== false ? (language === 'en' ? 'Deactivate' : 'Pasif Yap') : (language === 'en' ? 'Activate' : 'Aktif Yap')}
                    </button>
                  </RoleBasedComponent>
                  <button
                    onClick={() => printUserCargoLabel(user)}
                    className="flex items-center px-3 py-2 text-xs bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <Truck className="w-3 h-3 mr-1" />
                    {t('users.labelBtn')}
                  </button>
                  <RoleBasedComponent requiredPermissions={['users.delete']}>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="flex items-center px-3 py-2 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      {t('users.deleteBtn')}
                    </button>
                  </RoleBasedComponent>
                </div>
              </div>
            ))}
          </div>
        </>
      )}


      {/* Role Permissions Tab */}
      {activeTab === 'permissions' && (
        <RoleBasedComponent requiredRoles={['superadmin']}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('users.permissionsTitle')}</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t('users.permissionsDesc')}
              </p>
            </div>
            
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">{t('users.permColMenu')}</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                        <div>Calisan</div>
                        <div className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-1">G | E | D | S</div>
                      </th>
                      <th className="text-center py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                        <div>Teknisyen</div>
                        <div className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-1">G | E | D | S</div>
                      </th>
                      <th className="text-center py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                        <div>Admin</div>
                        <div className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-1">G | E | D | S</div>
                      </th>
                      <th className="text-center py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                        <div>SuperAdmin</div>
                        <div className="text-xs font-normal text-gray-500 dark:text-gray-400 mt-1">G | E | D | S</div>
                      </th>
                    </tr>
                    <tr className="border-b border-gray-100 bg-gray-50 dark:bg-gray-700">
                      <td className="py-2 px-4 text-xs text-gray-600 dark:text-gray-400">{t('users.permLegend')}</td>
                      <td colSpan={4} className="py-2 px-4 text-xs text-gray-500 dark:text-gray-400 text-center">{t('users.permEachBox')}</td>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {Array.isArray(menuItems) && menuItems.map((menu, index) => (
                      <React.Fragment key={menu.id}>
                        {/* Ana Menü */}
                        <tr className={index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-gray-700'}>
                          <td className="py-3 px-4 font-medium text-gray-900 dark:text-gray-100">{menu.label}</td>
                          {['calisan', 'teknisyen', 'admin', 'superadmin'].map(role => (
                            <td key={role} className="py-3 px-4 text-center">
                              {menu.children ? (
                                <input 
                                  type="checkbox" 
                                  className="rounded" 
                                  checked={backendRolePermissions[`${menu.id}_${role}_view`] ?? menu.role_access?.includes(role)} 
                                  onChange={(e) => handleRolePermissionChange(menu.id, role, 'view', e.target.checked)}
                                />
                              ) : (
                                <div className="flex justify-center space-x-1">
                                  <input 
                                    type="checkbox" 
                                    className="w-3 h-3" 
                                    checked={backendRolePermissions[`${menu.id}_${role}_view`] ?? menu.role_access?.includes(role)} 
                                    onChange={(e) => handleRolePermissionChange(menu.id, role, 'view', e.target.checked)}
                                    title="Görüntüle" 
                                  />
                                  <input 
                                    type="checkbox" 
                                    className="w-3 h-3" 
                                    checked={backendRolePermissions[`${menu.id}_${role}_create`] ?? menu.role_access?.includes(role)} 
                                    onChange={(e) => handleRolePermissionChange(menu.id, role, 'create', e.target.checked)}
                                    title="Ekle" 
                                  />
                                  <input 
                                    type="checkbox" 
                                    className="w-3 h-3" 
                                    checked={backendRolePermissions[`${menu.id}_${role}_update`] ?? menu.role_access?.includes(role)} 
                                    onChange={(e) => handleRolePermissionChange(menu.id, role, 'update', e.target.checked)}
                                    title="Düzenle" 
                                  />
                                  <input 
                                    type="checkbox" 
                                    className="w-3 h-3" 
                                    checked={backendRolePermissions[`${menu.id}_${role}_delete`] ?? menu.role_access?.includes(role)} 
                                    onChange={(e) => handleRolePermissionChange(menu.id, role, 'delete', e.target.checked)}
                                    title="Sil" 
                                  />
                                </div>
                              )}
                            </td>
                          ))}
                        </tr>
                        
                        {/* Alt Menüler */}
                        {menu.children?.map((child, childIndex) => (
                          <tr key={child.id} className={index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-gray-700'}>
                            <td className="py-3 px-4 pl-8 text-gray-700 dark:text-gray-300">{child.label}</td>
                            {['calisan', 'teknisyen', 'admin', 'superadmin'].map(role => (
                              <td key={role} className="py-3 px-4 text-center">
                                <div className="flex justify-center space-x-1">
                                  <input 
                                    type="checkbox" 
                                    className="w-3 h-3" 
                                    checked={backendRolePermissions[`${child.id}_${role}_view`] ?? child.role_access?.includes(role)} 
                                    onChange={(e) => handleRolePermissionChange(child.id, role, 'view', e.target.checked)}
                                    title="Görüntüle" 
                                  />
                                  <input 
                                    type="checkbox" 
                                    className="w-3 h-3" 
                                    checked={backendRolePermissions[`${child.id}_${role}_create`] ?? child.role_access?.includes(role)} 
                                    onChange={(e) => handleRolePermissionChange(child.id, role, 'create', e.target.checked)}
                                    title="Ekle" 
                                  />
                                  <input 
                                    type="checkbox" 
                                    className="w-3 h-3" 
                                    checked={backendRolePermissions[`${child.id}_${role}_update`] ?? child.role_access?.includes(role)} 
                                    onChange={(e) => handleRolePermissionChange(child.id, role, 'update', e.target.checked)}
                                    title="Düzenle" 
                                  />
                                  <input 
                                    type="checkbox" 
                                    className="w-3 h-3" 
                                    checked={backendRolePermissions[`${child.id}_${role}_delete`] ?? child.role_access?.includes(role)} 
                                    onChange={(e) => handleRolePermissionChange(child.id, role, 'delete', e.target.checked)}
                                    title="Sil" 
                                  />
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}

                  </tbody>
                </table>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button 
                  onClick={saveRolePermissions}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  {t('users.savePermissions')}
                </button>
              </div>
            </div>
          </div>
        </RoleBasedComponent>
      )}


      {/* User Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-5xl max-h-[98vh] sm:max-h-[95vh] overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                      {editingUser ? t('users.modalEditTitle') : t('users.modalNewTitle')}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {editingUser ? t('users.modalEditSubtitle') : t('users.modalNewSubtitle')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 rounded-lg transition-colors"
                  aria-label="Kapat"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Form Content */}
            <div className="p-6 space-y-8">
              {/* Basic Information */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-6">
                  <div className="p-1 bg-primary/20 rounded">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('users.sectionBasic')}</h3>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Ad Soyad */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={userData.name}
                      onChange={(e) => setUserData({...userData, name: e.target.value})}
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                      placeholder="Mehmet Demir"
                    />
                  </div>

                  {/* E-posta */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldEmail')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={userData.email}
                      onChange={(e) => setUserData({...userData, email: e.target.value})}
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                      placeholder="mehmet@company.com"
                    />
                  </div>

                  {/* Telefon */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldPhone')}
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 pointer-events-none">
                        +90
                      </span>
                      <input
                        type="text"
                        value={userData.phone.replace('+90 ', '')}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, '');

                          // Maksimum 10 haneli
                          if (value.length > 10) {
                            value = value.substring(0, 10);
                          }

                          // Format: 549 802 72 11
                          let formatted = '';
                          if (value.length > 0) {
                            if (value.length <= 3) {
                              formatted = value;
                            } else if (value.length <= 6) {
                              formatted = value.substring(0, 3) + ' ' + value.substring(3);
                            } else if (value.length <= 8) {
                              formatted = value.substring(0, 3) + ' ' + value.substring(3, 6) + ' ' + value.substring(6);
                            } else {
                              formatted = value.substring(0, 3) + ' ' + value.substring(3, 6) + ' ' + value.substring(6, 8) + ' ' + value.substring(8);
                            }
                          }

                          // State'e +90 ile birlikte kaydet
                          setUserData({...userData, phone: formatted ? '+90 ' + formatted : ''});
                        }}
                        className="w-full pl-12 pr-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        placeholder="549 802 72 11"
                      />
                    </div>
                  </div>

                  {/* Ünvan */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldTitle')}
                    </label>
                    <input
                      type="text"
                      value={userData.title}
                      onChange={(e) => setUserData({...userData, title: e.target.value})}
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                      placeholder="IT Uzmanı"
                    />
                  </div>

                  {/* Departman */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldDepartment')}
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={userData.department}
                        onChange={(e) => setUserData({...userData, department: e.target.value})}
                        className="flex-1 px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                      >
                        <option value="">{t('users.selectDepartment')}</option>
                        {departmentsData.map((dept, index) => (
                          <option key={index} value={dept}>
                            {dept}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowAddDepartmentModal(true)}
                        className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                        title="Yeni Departman Ekle"
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                  </div>

                  {/* Lokasyon */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldLocation')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={userData.location_id}
                      onChange={(e) => setUserData({...userData, location_id: parseInt(e.target.value)})}
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    >
                      <option value={0}>{t('users.selectLocation')}</option>
                      {locationsData.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Çoklu Lokasyon Seçimi (Admin, Teknisyen, Super Admin için) */}
                  {['admin', 'teknisyen', 'superadmin'].includes(userData.role) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('users.fieldAuthorizedLocations')}
                      </label>
                      <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 max-h-40 overflow-y-auto">
                        {locationsData.map((location) => (
                          <label key={location.id} className="flex items-center space-x-2 py-1">
                            <input
                              type="checkbox"
                              checked={userData.locations.includes(location.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setUserData({
                                    ...userData,
                                    locations: [...userData.locations, location.id]
                                  });
                                } else {
                                  setUserData({
                                    ...userData,
                                    locations: userData.locations.filter(id => id !== location.id)
                                  });
                                }
                              }}
                              className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{location.name}</span>
                          </label>
                        ))}
                      </div>
                      {userData.locations.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {(language === 'en' ? 'Selected locations: ' : 'Seçilen lokasyonlar: ')}{userData.locations.map(id =>
                              locationsData.find(loc => loc.id === id)?.name
                            ).join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Şirket */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldCompany')}
                    </label>
                    <select
                      value={userData.company_id}
                      onChange={(e) => setUserData({...userData, company_id: parseInt(e.target.value)})}
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    >
                      <option value={0}>{t('users.selectCompany')}</option>
                      {companiesData.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Yetki */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldRole')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={userData.role}
                      onChange={(e) => setUserData({...userData, role: e.target.value as UserType['role']})}
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    >
                      {getAvailableRoles().map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-6">
                  <div className="p-1 bg-green-100 rounded">
                    <Calendar className="w-4 h-4 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('users.sectionPersonal')}</h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Doğum Tarihi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldBirthDate')} <span className="text-gray-500 dark:text-gray-400 text-xs font-normal">{t('users.optionalDate')}</span>
                    </label>
                    <input
                      type="date"
                      value={userData.birth_date}
                      onChange={(e) => setUserData({...userData, birth_date: e.target.value})}
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    />
                  </div>

                  {/* İşe Başlama Tarihi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldHireDate')} <span className="text-gray-500 dark:text-gray-400 text-xs font-normal">{t('users.optionalDate')}</span>
                    </label>
                    <input
                      type="date"
                      value={userData.hire_date}
                      onChange={(e) => setUserData({...userData, hire_date: e.target.value})}
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                    />
                  </div>

                  {/* Acil Durum İletişim */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldEmergencyContact')}
                    </label>
                    <input
                      type="text"
                      value={userData.emergency_contact}
                      onChange={(e) => setUserData({...userData, emergency_contact: e.target.value})}
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                      placeholder="Eş/Aile Adı"
                    />
                  </div>

                  {/* Acil Durum Telefon */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldEmergencyPhone')}
                    </label>
                    <input
                      type="tel"
                      value={userData.emergency_phone}
                      onChange={(e) => setUserData({...userData, emergency_phone: e.target.value})}
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                      placeholder="+90 532 987 65 43"
                    />
                  </div>

                  {/* Adres */}
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldAddress')}
                    </label>
                    <textarea
                      value={userData.address}
                      onChange={(e) => setUserData({...userData, address: e.target.value})}
                      rows={3}
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none transition-colors"
                      placeholder="Tam adres bilgisi"
                    />
                  </div>
                </div>
              </div>

              {/* Password Section */}
              {!editingUser && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <div className="flex items-center space-x-2 mb-6">
                    <div className="p-1 bg-red-100 rounded">
                      <Lock className="w-4 h-4 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('users.sectionSecurity')}</h3>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('users.fieldPassword')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={userData.password}
                        onChange={(e) => setUserData({...userData, password: e.target.value})}
                        className="w-full px-4 py-3 pr-24 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                        placeholder="Güçlü parola girin"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center">
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="px-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 touch-manipulation transition-colors"
                          aria-label={showPassword ? 'Parolayı gizle' : 'Parolayı göster'}
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                        <button
                          type="button"
                          onClick={generatePassword}
                          className="px-3 py-1 text-primary hover:text-primary-dark text-xs font-medium touch-manipulation transition-colors"
                          aria-label="Otomatik parola oluştur"
                        >
                          {t('users.generatePassword')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}



              {/* Notes */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                <div className="flex items-center space-x-2 mb-6">
                  <div className="p-1 bg-yellow-100 rounded">
                    <FileText className="w-4 h-4 text-yellow-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('users.sectionNotes')}</h3>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('users.fieldNotes')}
                  </label>
                  <textarea
                    value={userData.notes}
                    onChange={(e) => setUserData({...userData, notes: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none transition-colors"
                    placeholder="Kullanıcı hakkında notlar..."
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800 rounded-b-lg">
              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="w-full sm:w-auto px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors text-base font-medium"
                >
                  {t('users.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  className="w-full sm:w-auto px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-base font-medium flex items-center justify-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{editingUser ? t('users.update') : t('users.save')}</span>
                </button>
              </div>
            </div>
          </div>
        </Modal>

      {/* User Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
          {selectedUser && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[98vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
              <div className="flex justify-between items-start">
                <div className="flex items-center flex-1">
                  <img
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-full"
                    src={selectedUser.avatar || 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop'}
                    alt={selectedUser.name}
                  />
                  <div className="ml-3 sm:ml-4 flex-1">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">
                      {selectedUser.name}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{selectedUser.title}</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${getRoleColor(selectedUser.role)}`}>
                      {getRoleLabel(selectedUser.role)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400 p-1 touch-manipulation"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 space-y-6">
              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('users.detailContactTitle')}</h3>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 text-gray-400 mr-3" />
                      <span className="text-sm text-gray-900 dark:text-gray-100">{selectedUser.email}</span>
                    </div>
                    {selectedUser.phone && (
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 text-gray-400 mr-3" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">{selectedUser.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 text-gray-400 mr-3" />
                      <span className="text-sm text-gray-900 dark:text-gray-100">{selectedUser.location?.name || 'Atanmamış'}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('users.detailWorkTitle')}</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('users.detailDepartment')}</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedUser.department || '-'}</span>
                    </div>
                    {selectedUser.hire_date && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('users.detailHireDate')}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {new Date(selectedUser.hire_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('users.detailStatus')}</span>
                      <span className={`text-sm font-medium ${selectedUser.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                        {selectedUser.is_active !== false ? t('users.statusActive') : t('users.statusInactive')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              {(selectedUser.birth_date || selectedUser.emergency_contact) && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('users.detailPersonalTitle')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedUser.birth_date && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('users.detailBirthDate')}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {new Date(selectedUser.birth_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                        </span>
                      </div>
                    )}
                    {selectedUser.emergency_contact && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('users.detailEmergencyContact')}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedUser.emergency_contact}</span>
                      </div>
                    )}
                    {selectedUser.emergency_phone && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('users.detailEmergencyPhone')}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedUser.emergency_phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Address */}
              {selectedUser.address && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('users.detailAddressTitle')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {selectedUser.address}
                  </p>
                </div>
              )}

              {/* Notes */}
              {selectedUser.notes && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('users.detailNotesTitle')}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {selectedUser.notes}
                  </p>
                </div>
              )}

              {/* Assigned Assets */}
              <div>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('users.detailAssetsTitle')}</h3>
                  {!assetsLoading && userAssets && (userAssets.gsmLines?.length > 0 || userAssets.computers?.length > 0 || userAssets.mobilePhones?.length > 0 || userAssets.monitors?.length > 0 || userAssets.equipmentAssignments?.length > 0 || userAssets.handheldTerminals?.length > 0) && (
                    <button
                      onClick={printUserAssets}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Printer size={15} />
                      {t('users.detailAssetsPrintBtn')}
                    </button>
                  )}
                </div>
                {assetsLoading ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400">{t('users.detailAssetsLoading')}</div>
                ) : userAssets ? (
                  <div className="space-y-4">
                    {/* GSM Lines */}
                    {userAssets.gsmLines?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('users.detailAssetsGsm')}</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColLineNo')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColTariff')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColOperator')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColStatus')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userAssets.gsmLines.map((line: any) => (
                                <tr key={line.id} className="border-t border-gray-100 dark:border-gray-600">
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{line.phone_number}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{line.tariff_name || '-'}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{line.company_name || '-'}</td>
                                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${line.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{line.status}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Computers */}
                    {userAssets.computers?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('users.detailAssetsComputers')}</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColInventory')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColBrandModel')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColSerial')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColLocation')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userAssets.computers.map((comp: any) => (
                                <tr key={comp.id} className="border-t border-gray-100 dark:border-gray-600">
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{comp.inventory_number || '-'}</td>
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{comp.brand} {comp.model}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{comp.serial_number || '-'}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{comp.location_name || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Mobile Phones */}
                    {userAssets.mobilePhones?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('users.detailAssetsMobilePhones')}</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColInventory')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColBrandModel')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColImei')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColOperator')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userAssets.mobilePhones.map((phone: any) => (
                                <tr key={phone.id} className="border-t border-gray-100 dark:border-gray-600">
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{phone.inventory_number || '-'}</td>
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{phone.brand} {phone.model}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{phone.imei1 || '-'}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{phone.company_name || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* IoT Equipment (El Terminalleri) */}
                    {userAssets.handheldTerminals?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{language === 'en' ? 'IoT Equipment' : 'IoT Ekipmanı'} ({userAssets.handheldTerminals.length})</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColInventory')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColBrandModel')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColSerial')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColLocation')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userAssets.handheldTerminals.map((h: any) => (
                                <tr key={h.id} className="border-t border-gray-100 dark:border-gray-600">
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{h.inventory_number || '-'}</td>
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{h.brand} {h.model}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{h.serial_number || '-'}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{h.location_name || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Monitors */}
                    {userAssets.monitors?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('users.detailAssetsMonitors')}</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColInventory')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColBrandModel')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColSerial')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColLocation')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userAssets.monitors.map((mon: any) => (
                                <tr key={mon.id} className="border-t border-gray-100 dark:border-gray-600">
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{mon.inventory_number || '-'}</td>
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{mon.brand} {mon.model}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{mon.serial_number || '-'}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{mon.location_name || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Equipment Assignments */}
                    {userAssets.equipmentAssignments?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('users.detailAssetsEquipment')}</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColCategory')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColBrandModel')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColQty')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColAssignDate')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userAssets.equipmentAssignments.map((eq: any) => (
                                <tr key={eq.id} className="border-t border-gray-100 dark:border-gray-600">
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{eq.category_name || '-'}</td>
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{eq.brand} {eq.model}</td>
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{eq.quantity}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{eq.assigned_date ? new Date(eq.assigned_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR') : '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Assignment Forms */}
                    {userAssets.assignmentForms?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('users.detailAssetsForms')}</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColFormNo')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColType')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColDevice')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColDate')}</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">{t('users.detailAssetsColDeliveredBy')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {userAssets.assignmentForms.map((form: any) => (
                                <tr key={form.id} className="border-t border-gray-100 dark:border-gray-600">
                                  <td className="px-3 py-2 font-mono text-gray-900 dark:text-gray-100">{form.form_number}</td>
                                  <td className="px-3 py-2">
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${form.form_type === 'assignment' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                                      {form.form_type === 'assignment' ? t('users.detailAssetsFormAssignment') : t('users.detailAssetsFormReturn')}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{form.device_brand ? `${form.device_brand} ${form.device_model}` : form.item_count ? t('users.detailAssetsItemCount', { count: form.item_count }) : '-'}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{form.assignment_date ? new Date(form.assignment_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR') : '-'}</td>
                                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{form.assigned_by_name}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {!userAssets.gsmLines?.length && !userAssets.computers?.length && !userAssets.mobilePhones?.length && !userAssets.monitors?.length && !userAssets.equipmentAssignments?.length && !userAssets.handheldTerminals?.length && !userAssets.assignmentForms?.length && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 italic">{t('users.detailAssetsEmpty')}</p>
                    )}

                    {(userAssets.gsmLines?.length > 0 || userAssets.computers?.length > 0 || userAssets.mobilePhones?.length > 0 || userAssets.monitors?.length > 0 || userAssets.equipmentAssignments?.length > 0 || userAssets.handheldTerminals?.length > 0) && (
                      <div className="pt-2">
                        <button
                          onClick={handleOpenReturnModal}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" /></svg>
                          {t('users.detailAssetsReturnBtn')}
                        </button>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* System Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('users.detailSystemTitle')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('users.detailCreatedAt')}</span>
                    <span className="font-medium">{new Date(selectedUser.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('users.detailUpdatedAt')}</span>
                    <span className="font-medium">{new Date(selectedUser.updated_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                  </div>
                  {selectedUser.last_login && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('users.detailLastLogin')}</span>
                      <span className="font-medium">{new Date(selectedUser.last_login).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('users.detailEmailVerified')}</span>
                    <span className={`font-medium ${selectedUser.email_verified ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedUser.email_verified ? t('users.detailYes') : t('users.detailNo')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* Return All Assets Modal */}
      <Modal isOpen={showReturnModal} onClose={() => { if (!returnLoading) setShowReturnModal(false); }} zIndex="z-[60]">
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg shadow-2xl">
          <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('users.returnModalTitle')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{selectedUser?.name}</p>
            </div>
            {!returnLoading && (
              <button onClick={() => setShowReturnModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={20} />
              </button>
            )}
          </div>

          <div className="p-5 space-y-4">
            {returnResult ? (
              /* Başarı durumu */
              <div className="text-center py-4 space-y-3">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <div>
                  <p className="text-base font-medium text-gray-900 dark:text-gray-100">{t('users.returnModalSuccessMsg')}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('users.returnModalFormLabel')} <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">{returnResult.formNumber}</span></p>
                </div>
                <div className="flex gap-3 justify-center pt-2">
                  <button
                    onClick={() => setShowReturnModal(false)}
                    className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t('users.returnModalCloseBtn')}
                  </button>
                  <button
                    onClick={() => {
                      setShowReturnModal(false);
                      setShowDetailModal(false);
                      window.dispatchEvent(new CustomEvent('navigate', { detail: `assignment-form-pdf/${returnResult.formId}` }));
                    }}
                    className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2"
                  >
                    <FileText size={15} />
                    {t('users.returnModalViewFormBtn')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Uyarı */}
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3 flex gap-2">
                  <svg className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                  <p className="text-sm text-orange-700 dark:text-orange-300">{t('users.returnModalWarning')}</p>
                </div>

                {/* İade edilecek kalemler */}
                {userAssets && (
                  <div className="space-y-2">
                    {userAssets.gsmLines?.length > 0 && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">{t('users.detailAssetsGsm')} ({userAssets.gsmLines.length})</p>
                        <div className="space-y-1">
                          {userAssets.gsmLines.map((g: any) => (
                            <p key={g.id} className="text-sm text-gray-800 dark:text-gray-200">{g.phone_number}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    {userAssets.computers?.length > 0 && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">{t('users.detailAssetsComputers')} ({userAssets.computers.length})</p>
                        <div className="space-y-1">
                          {userAssets.computers.map((c: any) => (
                            <p key={c.id} className="text-sm text-gray-800 dark:text-gray-200">{c.brand} {c.model}{c.inventory_number ? ` — ${c.inventory_number}` : ''}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    {userAssets.mobilePhones?.length > 0 && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">{t('users.detailAssetsMobilePhones')} ({userAssets.mobilePhones.length})</p>
                        <div className="space-y-1">
                          {userAssets.mobilePhones.map((p: any) => (
                            <p key={p.id} className="text-sm text-gray-800 dark:text-gray-200">{p.brand} {p.model}{p.inventory_number ? ` — ${p.inventory_number}` : ''}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    {userAssets.handheldTerminals?.length > 0 && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">{language === 'en' ? 'IoT Equipment' : 'IoT Ekipmanı'} ({userAssets.handheldTerminals.length})</p>
                        <div className="space-y-1">
                          {userAssets.handheldTerminals.map((h: any) => (
                            <p key={h.id} className="text-sm text-gray-800 dark:text-gray-200">{h.brand} {h.model}{h.inventory_number ? ` — ${h.inventory_number}` : ''}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    {userAssets.monitors?.length > 0 && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">{t('users.detailAssetsMonitors')} ({userAssets.monitors.length})</p>
                        <div className="space-y-1">
                          {userAssets.monitors.map((m: any) => (
                            <p key={m.id} className="text-sm text-gray-800 dark:text-gray-200">{m.brand} {m.model}{m.inventory_number ? ` — ${m.inventory_number}` : ''}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    {userAssets.equipmentAssignments?.length > 0 && (
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">{t('users.detailAssetsEquipment')} ({userAssets.equipmentAssignments.length})</p>
                        <div className="space-y-1">
                          {userAssets.equipmentAssignments.map((e: any) => (
                            <p key={e.id} className="text-sm text-gray-800 dark:text-gray-200">{e.brand} {e.model} — {e.quantity} adet</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Notlar */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('users.returnModalNotesLabel')}</label>
                  <textarea
                    value={returnNotes}
                    onChange={(e) => setReturnNotes(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                    placeholder={t('users.returnModalNotesPh')}
                  />
                </div>

                {/* Butonlar */}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => setShowReturnModal(false)}
                    disabled={returnLoading}
                    className="flex-1 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                  >
                    {t('users.returnModalCancelBtn')}
                  </button>
                  <button
                    onClick={handleReturnAllAssets}
                    disabled={returnLoading}
                    className="flex-1 px-4 py-2.5 text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {returnLoading ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                        {t('users.returnModalLoadingBtn')}
                      </>
                    ) : t('users.returnModalConfirmBtn')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Add Department Modal */}
      <Modal isOpen={showAddDepartmentModal} onClose={() => setShowAddDepartmentModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{t('users.addDeptTitle')}</h2>
              <button
                onClick={() => {
                  setShowAddDepartmentModal(false);
                  setNewDepartment('');
                }}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('users.deptNameLabel')}
                </label>
                <input
                  type="text"
                  value={newDepartment}
                  onChange={(e) => setNewDepartment(e.target.value)}
                  className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                  placeholder="Örn: İnsan Kaynakları"
                  autoFocus
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowAddDepartmentModal(false);
                  setNewDepartment('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600 transition-colors"
              >
                {t('users.cancel')}
              </button>
              <button
                onClick={handleAddDepartment}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2"
              >
                <Plus size={20} />
                {t('users.addDept')}
              </button>
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default UsersPage;
